import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Avatar,
  Box,
  Container,
  Flex,
  Grid,
  Heading,
  Skeleton,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { MdVideoLibrary } from "react-icons/md";
import Layout from "../../../components/student/Layout/layout";
import VideoCard from "../../../containers/student/video/video";
import AddVoucher from "../../../containers/student/models/add-voucher";
import { getSectionRequest } from "../../../modules/students-modules/sections/Actions";
import { getVideosByChapterIdSuccess } from "../../../modules/students-modules/videos/Actions";
import axiosInstance from "../../../helpers/api";
import defaultLogo from "../../../assets/logo.png";

const C = {
  primary: "#E85D04",
  secondary: "#9A3412",
  border: "#E8E0D5",
  paper: "#FFFFFF",
  mutedBg: "#F5F0E8",
  muted: "#78716C",
};

const BASE_URL = "https://asquera.com/asquera";

function chapterLogoUrl(logo) {
  if (!logo || logo === "null" || logo === "NULL" || logo === "undefined") {
    return defaultLogo;
  }
  if (String(logo).startsWith("http")) return logo;
  return `${BASE_URL}/${String(logo).replace(/^\//, "")}`;
}

const VideosChaptersList = () => {
  const [getSearchParams] = useSearchParams();
  const section_name = getSearchParams.get("name");
  const navigate = useNavigate();
  const { id } = useParams();
  const section = useSelector((state) => state.sections.section);
  const isLoading = useSelector((state) => state.sections.isLoading);
  const [expandedChapters, setExpandedChapters] = useState([]);
  const [allVideos, setAllVideos] = useState({});
  const [loadingChapters, setLoadingChapters] = useState(new Set());
  const [selectedVideoId, setSelectedVideoId] = useState(null);

  const dispatch = useDispatch();

  useEffect(() => {
    if (id) {
      setAllVideos({});
      setExpandedChapters([]);
      setLoadingChapters(new Set());
      dispatch(getSectionRequest(id));
    }
  }, [id, dispatch]);

  const handleChapterClick = async (chapterId) => {
    if (expandedChapters.includes(chapterId)) {
      setExpandedChapters((prev) => prev.filter((cid) => cid !== chapterId));
    } else {
      setExpandedChapters((prev) => [...prev, chapterId]);

      if (!allVideos[chapterId]) {
        try {
          setLoadingChapters((prev) => new Set(prev).add(chapterId));

          const response = await axiosInstance.get(`/students/videos/${chapterId}`);
          const videos = response.data.data || [];

          setAllVideos((prev) => ({ ...prev, [chapterId]: videos }));
          dispatch(getVideosByChapterIdSuccess({ data: videos }));
        } catch (error) {
          console.error(`Error fetching videos for chapter ${chapterId}:`, error);
          setAllVideos((prev) => ({ ...prev, [chapterId]: [] }));
          dispatch(getVideosByChapterIdSuccess({ data: [] }));
        } finally {
          setLoadingChapters((prev) => {
            const newSet = new Set(prev);
            newSet.delete(chapterId);
            return newSet;
          });
        }
      } else {
        const chapterVideos = allVideos[chapterId] || [];
        dispatch(getVideosByChapterIdSuccess({ data: chapterVideos }));
      }
    }
  };

  const handleVideoClick = (videoData, chapterName) => {
    setSelectedVideoId(videoData?.id);
    navigate(`/video/${videoData?.id}?name=${chapterName}&video=${videoData?.id}`);
  };

  const renderVideoContent = (chapterId) => {
    const isExpanded = expandedChapters.includes(chapterId);
    const chapterVideos = allVideos[chapterId] || [];
    const isLoadingChapter = loadingChapters.has(chapterId);
    const chapterName = section?.find((ch) => ch.id === chapterId)?.name;

    if (!isExpanded) return null;

    if (isLoadingChapter) {
      return (
        <Grid
          templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }}
          gap={4}
          p={5}
        >
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} h="280px" borderRadius="xl" />
          ))}
        </Grid>
      );
    }

    if (chapterVideos.length > 0) {
      return (
        <Box p={5}>
          <Grid
            templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }}
            gap={4}
          >
            {chapterVideos.map((video, idx) => (
              <VideoCard
                key={video?.id ?? idx}
                data={video}
                index={idx}
                isSelected={selectedVideoId === video?.id}
                handelClick={() => handleVideoClick(video, chapterName)}
              />
            ))}
          </Grid>
        </Box>
      );
    }

    return (
      <Flex direction="column" align="center" justify="center" p={8} color={C.muted} gap={2}>
        <MdVideoLibrary size={32} />
        <Text fontWeight="600" fontSize="sm" textAlign="center">
          You Didn&apos;t pay for this chapter or this chapter has no videos
        </Text>
      </Flex>
    );
  };

  return (
    <Layout color="main_1" bg="white" isLoading={isLoading}>
      <Container maxW="container.2xl" h="100%" my="100px" mb="165px" w="100%">
        <VStack spacing={8} h="100%" w="100%" alignItems="stretch">
          <Box w="100%" maxW="4xl" mx="auto">
            <Flex
              align="center"
              justify="space-between"
              borderBottomWidth="2px"
              borderColor={C.primary}
              pb={4}
              mb={8}
              flexWrap="wrap"
              gap={4}
            >
              <Heading
                fontSize={{ base: "xl", md: "2xl" }}
                fontWeight="800"
                color={C.primary}
                textTransform="uppercase"
                letterSpacing="tight"
              >
                {section_name || "Videos"}
              </Heading>
            </Flex>

            {isLoading && (!section || section.length === 0) ? (
              <VStack spacing={4} align="stretch">
                {[1, 2, 3].map((k) => (
                  <Skeleton key={k} h="72px" borderRadius="xl" />
                ))}
              </VStack>
            ) : !section?.length ? (
              <Text color={C.muted} fontWeight="600">
                No chapters available for this section yet.
              </Text>
            ) : (
              <Accordion allowMultiple>
                {section.map((chapter, index) => (
                  <AccordionItem
                    key={chapter?.id}
                    bg={C.paper}
                    borderWidth="1px"
                    borderColor={C.border}
                    borderRadius="xl"
                    mb={4}
                    overflow="hidden"
                  >
                    <AccordionButton
                      px={6}
                      py={5}
                      _hover={{ bg: C.mutedBg }}
                      borderBottomWidth="1px"
                      borderColor={C.border}
                      onClick={() => handleChapterClick(chapter?.id)}
                      disabled={loadingChapters.has(chapter?.id)}
                      opacity={loadingChapters.has(chapter?.id) ? 0.6 : 1}
                    >
                      <Flex flex={1} align="center" gap={4} textAlign="left">
                        <Avatar
                          name={chapter?.name}
                          src={chapterLogoUrl(chapter?.logo)}
                          borderRadius="lg"
                          size="md"
                          bg="transparent"
                          flexShrink={0}
                        />
                        <Text flex={1} fontWeight="700" fontSize="md" color={C.primary}>
                          {chapter?.name || `Chapter ${index + 1}`}
                        </Text>
                        {loadingChapters.has(chapter?.id) ? (
                          <Spinner size="sm" color={C.primary} />
                        ) : null}
                      </Flex>
                      <AccordionIcon color={C.primary} />
                    </AccordionButton>
                    <AccordionPanel p={0}>{renderVideoContent(chapter?.id)}</AccordionPanel>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </Box>
        </VStack>
      </Container>
      <AddVoucher video_id={selectedVideoId} />
    </Layout>
  );
};

export default VideosChaptersList;
